/**
 * Corporate Profile API — backend calls for the Corporate Profile page.
 *
 * Read:   getcorps              (single corporation + campuses)
 *         att_timezones fetchall (timezone dropdown for Add Campus)
 *         att_user_roles fetchall (impersonate role dropdown)
 * Write:  core_corporate save    (edit basic info)
 *         campus_add             (add new / migrate campus)
 */

import { apiFetch, apiTransaction } from '../../../../../services/api.js';

// ---------------------------------------------------------------------------
// READ operations
// ---------------------------------------------------------------------------

/**
 * Fetch a single corporation by ID.
 *
 * V1 procedure: getcorps with corp_filter
 * Returns array — caller should use result[0] for the single record.
 * Each record includes a `campuses` array of { campus_id, campus_name }.
 */
export function fetchCorporation(campusId, corpId) {
  return apiFetch({
    procedure: 'getcorps',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    payload: { corp_filter: corpId, campus_filter: '' },
    readOnly: true,
  });
}

/**
 * Fetch all timezones (for the Add Campus timezone dropdown).
 *
 * V1: Tbl: "att_timezones", procedure: "fetchall", reqType: "direct"
 */
export function fetchTimezones(campusId) {
  return apiFetch({
    procedure: 'fetchall',
    reqType: 'direct',
    table: 'att_timezones',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

/**
 * Fetch all user roles (for the Impersonate role dropdown).
 *
 * V1: Tbl: "att_user_roles", procedure: "fetchall", reqType: "direct"
 */
export function fetchUserRoles(campusId) {
  return apiFetch({
    procedure: 'fetchall',
    reqType: 'direct',
    table: 'att_user_roles',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

// ---------------------------------------------------------------------------
// WRITE operations
// ---------------------------------------------------------------------------

/**
 * Save edited corporation basic info.
 *
 * V1: mysave to core_corporate, PKey: corporate_id, method: POST
 *
 * @param {Array} payload    — [{ corporate_id, corporate_name, corporate_guid, ... }]
 * @param {Array} oldPayload — [{ corporate_id, corporate_name, ... }] for audit trail
 * @param {object} session   — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
/* /myrest -> mysql_transaction (docs/MYREST_MIGRATION_AUDIT.md section A): only these columns are written, for a campus the caller may act on. */
export function editCorporation(payload, oldPayload, session) {
  return apiTransaction({
    transType: 'corp_edit',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Add a new campus or migrate a campus from 2.0.
 *
 * V1 TransType: campus_add
 *
 * @param {object} campusData — { campus_name, corporate_id, plan_expire, campus_guid, address, city, state, zipcode, timezone_id, country, migrate }
 * @param {object} session    — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
export function addCampus(campusData, session) {
  return apiTransaction({
    // 4.0 campuses are migrated in via an existing GUID (campus_add); 5.0 campuses
    // are created fresh on the cloud platform with no GUID (cloud_campus_add).
    // Mirrors campusesApi.addCampus so the Add Campus drawer routes 5.0 correctly.
    transType: campusData?.campus_guid ? 'campus_add' : 'cloud_campus_add',
    payload: [campusData],
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}
